import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductCodeSubDto } from './product-code-sub-service';
import { formatRecordedAt, resolveRecordedBy } from '../../../shared/commons/audit-display.util';
import { ProductCodeDto, ProductCodeService } from '../product-code/product-code-service';
import { compareByNumericValue } from '../../../shared/commons/select-compare.util';

@Component({
  selector: 'app-product-code-sub-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './product-code-sub-modal.html',
  styleUrl: './product-code-sub-modal.scss'
})
export class ProductCodeSubModal implements OnInit {
  @Input() item: ProductCodeSubDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  isProductCodeLoading = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  productCodes: ProductCodeDto[] = [];
  readonly compareByNumericValue = compareByNumericValue;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private productCodeService: ProductCodeService
  ) {
    this.form = this.fb.group({
      id: [null],
      productId: [null, [Validators.required]],
      value: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    if (this.item) {
      this.isEditMode = true;
      this.form.patchValue(this.item);
      this.recordedByDisplay = resolveRecordedBy(this.item);
      this.recordedAtDisplay = formatRecordedAt(this.item);
    } else {
      this.form.reset({ productId: null, value: '' });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    if (this.viewOnly) {
      this.form.disable();
    }

    this.loadProductCodes();
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    void this.submitAndClose(this.form.value);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private loadProductCodes(): void {
    this.isProductCodeLoading = true;
    this.productCodeService.getActiveProductCodes().subscribe({
      next: (items) => {
        this.productCodes = [...items].sort((a, b) => (a.productCode ?? '').localeCompare(b.productCode ?? ''));
        const selectedProductId = this.toNumberOrNull(this.form.get('productId')?.value)
          ?? this.toNumberOrNull(this.item?.productId);
        this.form.get('productId')?.setValue(selectedProductId, { emitEvent: false });
        this.isProductCodeLoading = false;
      },
      error: () => {
        this.productCodes = [];
        this.isProductCodeLoading = false;
      }
    });
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
